/* eslint-disable @typescript-eslint/no-unused-vars */
import TitleLayout from '@/components/Layout/TitleLayout';
import Banner from '@oncolens/oncolens-ui/lib/components/Common/Banner/Banner';
import { BannerType } from '@oncolens/oncolens-ui/lib/components/Common/Banner/Banner.helpers';
import Button from '@oncolens/oncolens-ui/lib/components/FormControls/Button';
import ButtonWithLoading from '@oncolens/oncolens-ui/lib/components/FormControls/ButtonWithLoading';
import Checkbox from '@oncolens/oncolens-ui/lib/components/FormControls/Checkbox';
import Combobox, {
  ComboboxItem,
} from '@oncolens/oncolens-ui/lib/components/FormControls/Combobox/Combobox';
import DefaultButton, {
  DefaultButtonColor,
} from '@oncolens/oncolens-ui/lib/components/FormControls/DefaultButton';
import RadioButtons from '@oncolens/oncolens-ui/lib/components/FormControls/RadioButtons';
import TextInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextInput';
import FormField from '@oncolens/oncolens-ui/lib/libraries/FormField';
import useFormStore from '@oncolens/oncolens-ui/lib/libraries/useFormStore';
import { always, assign, cloneDeep, compose, concat, omit, without } from 'lodash/fp';
import { nanoid } from 'nanoid';
import React, { useEffect, useReducer, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useNavigate } from 'react-router';
import * as yup from 'yup';
import shallow from 'zustand/shallow';
import { GroupFormValues, createGroup, updateGroup } from '../api/qip';
import { useHydrateGroup } from '../qip-metrics.hooks';

const schema = yup.object({
  name: yup.string().required('Name is required'),
  displayName: yup.string().nullable(),
  status: yup.string().required('Status is required.'),
  organizationIds: yup
    .array(yup.object().shape({ label: yup.string(), value: yup.string() }))
    .when('assignAllOrganizations', {
      is: (allSelected: boolean) => !allSelected,
      // eslint-disable-next-line @typescript-eslint/no-shadow
      then: (schema) => schema.min(1, 'At least one organization is required.').required(),
    }),
});

const defaultValues = {
  name: '',
  displayName: '',
  status: 'Inactive',
  organizationIds: null,
  questionIds: null,
} as GroupFormValues;

type State = {
  questions: {
    [questionId: string]: ComboboxItem;
  };
  conditions: {
    [conditionId: string]: {
      questionId: string;
      choiceToTriggerId: ComboboxItem;
      questionToShowId: ComboboxItem;
    };
  };
  selectedQuestions: string[];
  selectedConditions: string[];
};

export const QuestionGroupBuilder = () => {
  const formStore = useFormStore(defaultValues, schema);
  const [updateStore, isValid, questionIds] = formStore(
    (x) => [x.updateStore, x.isValid, x.questionIds],
    shallow,
  );
  const { groupId, group, orgData, questionData, choiceData, conditionData } =
    useHydrateGroup(updateStore);

  const [saving, setSaving] = useState(false);

  const [state, updateState] = useReducer(
    (accumulator: State, current: Partial<State>) => ({ ...accumulator, ...current }),
    {
      questions: {},
      conditions: {},
      selectedQuestions: [],
      selectedConditions: [],
    },
  );

  useEffect(() => {
    if (groupId) {
      updateState({
        questions: assign(
          state.questions,
          Object.fromEntries(
            questionData
              .slice()
              .filter((q) => questionIds?.includes(q.value as any))
              .sort(
                (a, b) =>
                  (group?.questionIds?.indexOf(a.value) ?? -1) -
                  (group?.questionIds?.indexOf(b.value) ?? -1),
              )
              .map((q) => [q.value, q]),
          ),
        ),
        conditions: assign(
          state.conditions,
          Object.fromEntries(conditionData.map((c) => [c.id, c])),
        ),
      });
    }
  }, [conditionData, groupId, questionData, questionIds]);

  const navigate = useNavigate();

  const onSave = () => {
    const store = formStore.getState();

    if (!store.isValid) return;

    setSaving(true);

    const formData = store.getValues();
    const { name, displayName, status } = formData;

    const data = {
      name,
      displayName: displayName || null,
      status,
      organizationIds: formData.organizationIds?.map((x) => x.value),
      questionIds: Object.values(state.questions).map((x) => x.value),
      conditions: Object.entries(state.conditions).map(([id, value]) => ({
        ...(!id.includes('nanoid') && { id }),
        questionId: state.questions[value.questionId].value,
        choiceToTriggerId: value.choiceToTriggerId?.value,
        questionToShowId: value.questionToShowId.value,
      })),
    } as any;

    if (!groupId) {
      createGroup(data)
        .then(() => {
          setSaving(false);
          navigate('/behavior-config/qip-metric-builder/question-groups');
        })
        .catch((error) => {
          setSaving(false);
          console.error(error);
        });
    } else {
      updateGroup(groupId as string, { id: groupId, ...data })
        .then(() => {
          setSaving(false);
          navigate('/behavior-config/qip-metric-builder/question-groups');
        })
        .catch((error) => {
          setSaving(false);
          console.error(error);
        });
    }
  };

  return (
    <TitleLayout title={`${groupId ? 'Edit' : 'Add'} Group`}>
      <div className="flex flex-col flex-grow overflow-y-auto bg-white rounded-lg shadow-lg max-w-7xl">
        <div className="grid grid-cols-3 gap-3 px-4 pt-4 mb-4 flex-grow-1">
          <FormField name="name" store={formStore}>
            {({ value, onChange }) => (
              <TextInput
                id="name"
                label="Name"
                htmlName="name"
                value={value}
                onChange={onChange}
                required
                type="text"
                testSelector="qip-group-builder-name"
              />
            )}
          </FormField>
          <FormField name="displayName" store={formStore}>
            {({ value, onChange }) => (
              <TextInput
                id="displayName"
                label="Display Name"
                htmlName="displayName"
                value={value}
                onChange={onChange}
                type="text"
                testSelector="qip-group-builder-display-name"
              />
            )}
          </FormField>
          <FormField name="organizationIds" store={formStore}>
            {({ value, onChange }) => {
              const showBanner = value?.length === orgData.length;

              return (
                <div>
                  <Combobox
                    id="organizations"
                    htmlName="organizations"
                    label="Organizations"
                    data={orgData}
                    value={value}
                    onChange={onChange}
                    useAutocomplete
                    useMultipleSelect
                    alignDropdown="left"
                  />
                  {showBanner && (
                    <Banner type={BannerType.Warning} className="mt-2 text-xs">
                      Any future organizations will not be included when selecting this function. To
                      ensure future organizations are included, select the checkbox under this
                      message titled &apos;Select all organizations&apos;.
                    </Banner>
                  )}
                </div>
              );
            }}
          </FormField>
        </div>
        <hr className="mx-4" />
        {Object.keys(state.questions) && (
          <div className="grid grid-cols-3 gap-3 pt-4 flex-grow-1">
            <div className="col-span-2 text-right">
              <DefaultButton
                disabled={!state.selectedQuestions.length && !state.selectedConditions.length}
                color={DefaultButtonColor.Neutral}
                textOnly
                onClick={() =>
                  updateState({
                    questions: omit(state.selectedQuestions, state.questions),
                    conditions: omit(state.selectedConditions, state.conditions),
                    selectedQuestions: without(state.selectedConditions, state.selectedQuestions),
                    selectedConditions: without(state.selectedConditions, state.selectedQuestions),
                  })
                }>
                Delete selected
              </DefaultButton>
            </div>
          </div>
        )}
        <div>
          {Object.keys(state.questions).map((id, i) => (
            <div key={id}>
              <div className="flex items-center flex-grow w-3/4">
                <Checkbox
                  id={`qip-question-checkbox-${i}`}
                  htmlName={`qip-question-checkbox-${i}`}
                  label=""
                  onClick={() =>
                    state.selectedQuestions.includes(id)
                      ? updateState({
                          selectedQuestions: without([id], state.selectedQuestions),
                          selectedConditions: without(
                            Array.from(
                              Object.entries(state.conditions)
                                .filter(([cid, c]) => c.questionId === id)
                                .map(([cid, _]) => cid),
                            ),
                            state.selectedConditions,
                          ),
                        })
                      : updateState({
                          selectedQuestions: concat([id], state.selectedQuestions),
                          selectedConditions: concat(
                            Array.from(
                              Object.entries(state.conditions)
                                .filter(([cid, c]) => c.questionId === id)
                                .map(([cid, _]) => cid),
                            ),
                            state.selectedConditions,
                          ),
                        })
                  }
                  value={state.selectedQuestions.includes(id)}
                  wrapperProps={{ className: 'm-auto ml-2' }}
                />
                <Combobox
                  id={`qip-question-select-${i}`}
                  htmlName={`qip-question-select-${i}`}
                  testSelector={`qip-question-select-${i}`}
                  label=""
                  data={questionData.map((q) =>
                    !Object.values(state.questions).includes(q) ? q : { ...q, disabled: true },
                  )}
                  value={state.questions[id]}
                  onChange={(e: any) =>
                    updateState({ questions: assign(state.questions, { [id]: e }) })
                  }
                  useAutocomplete
                  wrapperProps={{ className: 'flex-1' }}
                />
                <div className="ml-2">
                  <DefaultButton
                    className="mt-8"
                    color={DefaultButtonColor.Neutral}
                    disabled={
                      (state as any).questions?.[id]?.type === 'FreeText' ||
                      !(state as any).questions?.[id]?.value
                    }
                    testSelector={`qip-question-add-logic-${i}`}
                    textOnly
                    onClick={compose(
                      updateState,
                      always({
                        conditions: assign(state.conditions, {
                          [`nanoid-${nanoid()}`]: {
                            questionId: id,
                          },
                        }),
                      }),
                    )}>
                    Add logic
                  </DefaultButton>
                </div>
              </div>
              {Object.entries(state.conditions)
                .filter(([_, x]) => x.questionId === id)
                .map(([cid, _], j) => (
                  <div key={j} className="flex flex-grow w-3/4 ml-8">
                    <Checkbox
                      wrapperProps={{ className: 'mt-8' }}
                      id={`qip-condition-checkbox-${i}-${j}`}
                      htmlName={`qip-condition-checkbox-${i}-${j}`}
                      label=""
                      onClick={() =>
                        state.selectedConditions.includes(cid)
                          ? updateState({
                              selectedConditions: without([cid], state.selectedConditions),
                            })
                          : updateState({
                              selectedConditions: concat([cid], state.selectedConditions),
                            })
                      }
                      value={state.selectedConditions.includes(cid)}
                    />
                    <span className="mt-8">If</span>
                    <Combobox
                      id={`qip-condition-select-${i}-${j}`}
                      htmlName={`qip-condition-select-${i}-${j}`}
                      testSelector={`qip-condition-choice-select-${i}-${j}`}
                      label=""
                      data={choiceData.filter((c) => c.questionId === state.questions?.[id]?.value)}
                      value={state.conditions[cid].choiceToTriggerId}
                      onChange={(e: any) => {
                        const condition = cloneDeep(state.conditions[cid]);
                        condition.choiceToTriggerId = e;
                        return updateState({
                          conditions: assign(state.conditions, { [cid]: condition }),
                        });
                      }}
                      wrapperProps={{ className: 'flex-1 ml-2' }}
                    />
                    <span className="mt-8 ml-4">Ask</span>
                    <Combobox
                      id={`qip-condition-trigger-select-${i}-${j}`}
                      htmlName={`qip-condition-trigger-select-${i}-${j}`}
                      testSelector={`qip-condition-trigger-select-${i}-${j}`}
                      label=""
                      data={questionData.map((q) =>
                        !Object.values(state.questions).includes(q) ? q : { ...q, disabled: true },
                      )}
                      value={state.conditions[cid].questionToShowId}
                      onChange={(e: any) => {
                        const condition = cloneDeep(state.conditions[cid]);
                        condition.questionToShowId = e;
                        return updateState({
                          conditions: assign(state.conditions, { [cid]: condition }),
                        });
                      }}
                      useAutocomplete
                      wrapperProps={{ className: 'flex-1 ml-2' }}
                    />
                  </div>
                ))}
            </div>
          ))}
          <div className="grid grid-cols-12 col-span-2 mt-4">
            <div className="col-span-3 col-start-2">
              <DefaultButton
                color={DefaultButtonColor.Neutral}
                textOnly
                onClick={compose(
                  updateState,
                  always({ questions: assign(state.questions, { [`nanoid-${nanoid()}`]: {} }) }),
                )}
                testSelector="qip-group-add-question-button">
                + Add a new question
              </DefaultButton>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 px-4 pt-4 flex-grow-1">
          <FormField name="status" store={formStore}>
            {({ value, onChange }) => (
              <RadioButtons
                label="Status"
                value={value}
                onChange={onChange}
                options={[
                  {
                    label: 'Active',
                    value: 'Active',
                  },
                  {
                    label: 'Inactive',
                    value: 'Inactive',
                  },
                ]}
              />
            )}
          </FormField>
        </div>
        <div className="flex items-center w-full pb-4 pr-4 min-h-fit">
          <Button
            className="w-auto px-6 ml-auto mr-3"
            buttonColor="gray"
            onClick={() => navigate(-1)}
            testSelector="qip-group-builder-back-button">
            Back
          </Button>
          <ButtonWithLoading
            loading={saving}
            className="w-auto px-6"
            buttonColor="blue"
            onClick={onSave}
            disabled={!isValid}
            testSelector="qip-group-builder-save-button">
            Save
          </ButtonWithLoading>
        </div>
      </div>
    </TitleLayout>
  );
};
